import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Target as TargetIcon, Save } from "lucide-react";

const monthKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const Targets = () => {
  const { receptionists } = useOutletContext();
  const [empId, setEmpId] = useState("");
  const [mk, setMk] = useState(monthKey());
  const [profileFields, setProfileFields] = useState(null);
  const [targets, setTargets] = useState({});

  useEffect(() => {
    api.get("/meta/profile-fields").then((r) => setProfileFields(r.data));
  }, []);

  useEffect(() => {
    if (!empId && receptionists?.length > 0) {
      setEmpId(receptionists[0].employee_id);
    }
  }, [receptionists]);

  const currentRec = useMemo(
    () => receptionists.find((r) => r.employee_id === empId),
    [empId, receptionists]
  );

  const fields = useMemo(() => {
    if (!profileFields || !currentRec) return [];
    return [...profileFields.shared, ...(profileFields.by_profile[currentRec.profile_type] || [])];
  }, [profileFields, currentRec]);

  useEffect(() => {
    if (!empId) return;
    api.get(`/targets?employee_id=${empId}&month_key_q=${mk}`).then((r) => {
      const map = {};
      r.data.forEach((t) => {
        map[t.metric_key] = { monthly_target: t.monthly_target, weight: t.weight };
      });
      setTargets(map);
    });
  }, [empId, mk]);

  const update = (key, field, val) => {
    setTargets((t) => ({
      ...t,
      [key]: { ...(t[key] || {}), [field]: val === "" ? "" : Number(val) },
    }));
  };

  const save = async () => {
    if (!empId) return;
    try {
      await api.post("/targets/bulk", {
        employee_id: empId,
        month_key: mk,
        targets,
      });
      toast.success("Targets saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Target Setting</div>
        <h1 className="font-heading text-4xl text-white tracking-tight mt-1">Monthly Targets</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Set monthly targets per metric for each receptionist. Weekly and daily targets auto-derive proportionally.
          Weights determine each field's contribution to the overall weighted score.
        </p>
      </div>

      <Card className="bg-[#1E293B] border-[#334155] p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">Receptionist</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="target-emp-select" className="bg-[#0F172A] border-[#334155] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                {receptionists.map((r) => (
                  <SelectItem key={r.employee_id} value={r.employee_id}>
                    {r.name} · {r.profile_type.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">Month (YYYY-MM)</Label>
            <Input
              data-testid="target-month"
              value={mk}
              onChange={(e) => setMk(e.target.value)}
              className="bg-[#0F172A] border-[#334155] text-white font-mono"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={save}
              data-testid="target-save-btn"
              className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium h-11"
            >
              <Save size={16} className="mr-2" /> Save Targets
            </Button>
          </div>
        </div>
      </Card>

      {currentRec && (
        <Card className="bg-[#1E293B] border-[#334155] p-6">
          <div className="flex items-center gap-2 mb-5">
            <TargetIcon size={18} className="text-[#C9A24B]" />
            <h3 className="font-heading text-xl text-white">{currentRec.name} · Targets</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fields.map((f) => {
              const t = targets[f.key] || {};
              const monthly = Number(t.monthly_target) || 0;
              return (
                <div
                  key={f.key}
                  className="p-4 rounded-lg bg-[#0F172A] border border-[#334155] space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-white">{f.label}</div>
                    <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500">
                      {f.unit}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1 block">
                        Monthly Target
                      </Label>
                      <Input
                        data-testid={`target-${f.key}`}
                        type="number"
                        value={t.monthly_target ?? ""}
                        onChange={(e) => update(f.key, "monthly_target", e.target.value)}
                        className="bg-[#1E293B] border-[#334155] text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1 block">
                        Weight %
                      </Label>
                      <Input
                        data-testid={`weight-${f.key}`}
                        type="number"
                        value={t.weight ?? f.weight}
                        onChange={(e) => update(f.key, "weight", e.target.value)}
                        className="bg-[#1E293B] border-[#334155] text-white"
                      />
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono flex justify-between">
                    <span>Weekly ~{Math.round(monthly / 4)}</span>
                    <span>Daily ~{Math.round(monthly / 26)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default Targets;
